import {
  HttpErrorResponse,
  HttpInterceptorFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';

import { AuthStateService } from '../auth/auth-state.service';

export const apiSessionInterceptor: HttpInterceptorFn = (request, next) => {
  if (!request.url.startsWith('/api/')) {
    return next(request);
  }

  const authState = inject(AuthStateService);
  const router = inject(Router);

  const headers: Record<string, string> = {
    'X-Request-Id': createRequestId(),
  };

  if (requiresCsrfToken(request.method)) {
    const csrfToken = readCookie('XSRF-TOKEN');

    if (csrfToken) {
      headers['X-XSRF-TOKEN'] = csrfToken;
    }
  }

  const apiRequest = request.clone({
    withCredentials: true,
    setHeaders: headers,
  });

  return next(apiRequest).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && shouldHandleExpiredSession(request.url)) {
        const returnUrl = normalizeReturnUrl(router.url);

        authState.setAnonymous();

        void router.navigate(['/login'], {
          queryParams: { returnUrl },
        });
      }

      return throwError(() => error);
    }),
  );
};

function requiresCsrfToken(method: string): boolean {
  const normalizedMethod = method.toUpperCase();

  return normalizedMethod !== 'GET'
    && normalizedMethod !== 'HEAD'
    && normalizedMethod !== 'OPTIONS'
    && normalizedMethod !== 'TRACE';
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const prefix = `${name}=`;

  for (const cookie of document.cookie.split(';')) {
    const normalizedCookie = cookie.trim();

    if (normalizedCookie.startsWith(prefix)) {
      return decodeURIComponent(normalizedCookie.substring(prefix.length));
    }
  }

  return null;
}

function shouldHandleExpiredSession(url: string): boolean {
  return !url.endsWith('/api/auth/login')
    && !url.endsWith('/api/auth/me')
    && !url.endsWith('/api/auth/csrf');
}

function normalizeReturnUrl(url: string): string {
  if (!url.startsWith('/') || url.startsWith('//') || url === '/login') {
    return '/';
  }

  return url;
}

function createRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `web-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}